import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import useDynamicImports, { LoadingStrategy } from '../../hooks/use-dynamic-imports';
import { FranchiseData } from '../../sourcing/source-nodes/sources/franchises';
import initCommunicationModule from './init-communication';
import type { LivePreviewConnectionState, LivePreviewError } from './types';
import { ENVIRONMENT } from '../../constants';

// Build-time gate. On the prod build (where ENVIRONMENT.IS_DRAFTS is not 'true'),
// webpack's DefinePlugin substitutes this to `false` and the whole
// live-preview branch gets tree-shaken out, including the dynamic import
// for `./init-communication` and its transitive dependency on
// `@contentstack/advanced-post-message`.
const LIVE_PREVIEW_ENABLED = ENVIRONMENT.IS_DRAFTS;

export type UseLiveDataTransformResult<T> = {
    data: T;
    isPreview: boolean;
    franchises: FranchiseData[];
    hasUpdatedFromCs: boolean;
    error: LivePreviewError | null;
    connectionState: LivePreviewConnectionState;
};

const useLiveDataTransform = <T extends Record<string, unknown>>(
    data: T,
): UseLiveDataTransformResult<T> => {
    const { pathname } = useLocation();
    const [isPreview, setIsPreview] = React.useState<boolean>(false);
    const [hasInitialized, setHasInitialized] = React.useState<boolean>(false);
    const [
        dataPartial,
        setDataPartial,
    ] = React.useState<Partial<T> | null>(null);
    const [
        franchises,
        setFranchises,
    ] = React.useState<FranchiseData[]>([]);
    const [
        currentLoadedPathname,
        setCurrentLoadedPathname,
    ] = React.useState<string | null>(null);
    const [error, setError] = React.useState<LivePreviewError | null>(null);
    const [connectionState, setConnectionState] = React.useState<LivePreviewConnectionState>('idle');

    // IMPORTANT: when LIVE_PREVIEW_ENABLED is the literal `false` at build time,
    // the body of this conditional (including the dynamic import) is dead code.
    // We still return early below so the hook is a no-op in prod.
    const {
        hasLoaded,
        scripts: { initCommunication },
    } = useDynamicImports(
        LIVE_PREVIEW_ENABLED
            ? {
                initCommunication: async () => {
                    const { default: pkg } = await import('./init-communication');
                    return pkg;
                },
            }
            : ({} as { initCommunication: () => Promise<typeof initCommunicationModule> }),
        LoadingStrategy.InIframe,
    );

    React.useEffect(() => {
        if (!LIVE_PREVIEW_ENABLED) return;

        // Route-change watch: if the user (or CS) has navigated, update the
        // tracked pathname so downstream state merges are keyed correctly.
        if (hasLoaded && currentLoadedPathname !== pathname && hasInitialized) {
            setCurrentLoadedPathname(pathname);
        }

        if (!hasInitialized && hasLoaded && initCommunication) {
            setIsPreview(true);
            setHasInitialized(true);
            const setPathname = (nextPathname: string): void => {
                if (currentLoadedPathname !== nextPathname) {
                    setCurrentLoadedPathname(nextPathname);
                    navigate(nextPathname);
                }
            };
            initCommunication({
                pathname,
                setPathname,
                setData: setDataPartial,
                setFranchises,
                setError,
                setConnectionState,
            });
        }
    }, [currentLoadedPathname, dataPartial, hasInitialized, hasLoaded, initCommunication, pathname]);

    if (!LIVE_PREVIEW_ENABLED) {
        return {
            data,
            isPreview: false,
            franchises: [],
            hasUpdatedFromCs: false,
            error: null,
            connectionState: 'idle',
        };
    }

    if (dataPartial && data !== undefined && currentLoadedPathname === pathname && hasInitialized) {
        return {
            data: {
                ...data,
                ...dataPartial,
            },
            isPreview,
            franchises,
            hasUpdatedFromCs: true,
            error,
            connectionState,
        };
    }

    return {
        data,
        isPreview,
        franchises,
        hasUpdatedFromCs: false,
        error,
        connectionState,
    };
};

export default useLiveDataTransform;
